/** Data processing, animation, and marker rendering helpers for FleetWorldMarkers. */
import type { ShaderProgram } from '../renderer/ShaderProgram';
import type { Camera } from '../renderer/Camera';
import type { TravelConfig } from '../config/TravelConfig';
import type { ActiveTravel } from '../ui/TravelMenuState';
import { axialToWorld } from '../math/HexMath';
import { SHIP_TYPE_COUNT } from '../config/ShipTypes';
import type { InstancedShipRenderer } from './InstancedShipRenderer';
import type { FleetFormation, TypeSlice } from '../systems/FleetFormation';
import { TravelAnimator } from '../systems/TravelAnimator';
import { buildTravelRoute } from '../systems/TravelRouteBuilder';
import type { TravelRoute } from '../systems/TravelTypes';
import { TravelMode } from '../systems/TravelTypes';

const FLOATS_PER_MAT = 16;
const FLIGHT_HEIGHT = 0.9;
const MAX_FLEETS = 12;
const MAX_CATCHUP_HOPS = 500;

export interface StationedMarker { wx: number; wz: number; }

export interface TravelingFleetData {
  travelId: string;
  wx: number;
  wz: number;
  dirX: number;
  dirZ: number;
  turnRate: number;
  ships: number[];
  animator: TravelAnimator | null;
  routeHexes: { q: number; r: number }[] | null;
  routeBuilt: TravelRoute | null;
  finished: boolean;
}

/** Map to preserve animators across data refreshes, keyed by travel ID. */
const _animatorCache = new Map<string, { animator: TravelAnimator; route: TravelRoute }>();

/** Per-array dirty-check keys (WeakMap so multiple callers don't collide). */
const _prevKeys = new WeakMap<TravelingFleetData[] | StationedMarker[], string>();

/**
 * Reconcile traveling/stationed fleet data from ActiveTravel[].
 * Uses dirty-check: only rebuilds arrays when fleet IDs change.
 * Returns true if arrays were rebuilt.
 */
export function applyFleetData(
  traveling: ActiveTravel[], stationed: ActiveTravel[], hexRadius: number,
  travelConfig: TravelConfig,
  outTraveling: TravelingFleetData[], outStationed: StationedMarker[],
  planetKeys: ReadonlySet<number> | null = null,
): boolean {
  // ── Dirty check: only rebuild if fleet list changed ──
  const tKey = traveling.map(t => t.id).join(',');
  const sKey = stationed.map(s => s.id).join(',');
  const travelingDirty = tKey !== (_prevKeys.get(outTraveling) ?? '');
  const stationedDirty = sKey !== (_prevKeys.get(outStationed) ?? '');

  if (!travelingDirty && !stationedDirty) return false;

  if (travelingDirty) {
    _prevKeys.set(outTraveling, tKey);
    const activeIds = new Set<string>();

    // Build lookup of existing fleet data for reuse
    const existing = new Map<string, TravelingFleetData>();
    for (const f of outTraveling) existing.set(f.travelId, f);

    outTraveling.length = 0;
    for (let i = 0; i < traveling.length; i++) {
      const t = traveling[i];
      activeIds.add(t.id);

      // Reuse existing fleet data object if possible (stable reference)
      const prev = existing.get(t.id);
      if (prev && prev.animator) {
        // Fleet already tracked with animator — keep it, just update ships
        prev.ships = t.ships;
        outTraveling.push(prev);
        continue;
      }

      if (t.route && t.route.length >= 2) {
        // ── Route-based animation ──────────────────────
        const entry = _getOrCreateAnimator(t, hexRadius, travelConfig, planetKeys);
        if (entry) {
          outTraveling.push({
            travelId: t.id, wx: 0, wz: 0, dirX: 0, dirZ: -1, turnRate: 0,
            ships: t.ships, animator: entry.animator,
            routeHexes: t.route, routeBuilt: entry.route, finished: false,
          });
          continue;
        }
      }

      // ── Lerp fallback (no route) ─────────────────────
      const [sx, sz] = axialToWorld(t.start.q, t.start.r, hexRadius);
      const [ex, ez] = axialToWorld(t.end.q, t.end.r, hexRadius);
      const p = Math.max(0, Math.min(1, t.progress));
      const wx = sx + (ex - sx) * p;
      const wz = sz + (ez - sz) * p;
      let dx = ex - sx, dz = ez - sz;
      const len = Math.sqrt(dx * dx + dz * dz);
      if (len > 1e-6) { dx /= len; dz /= len; } else { dx = 0; dz = -1; }
      outTraveling.push({ travelId: t.id, wx, wz, dirX: dx, dirZ: dz, turnRate: 0, ships: t.ships, animator: null, routeHexes: null, routeBuilt: null, finished: false });
    }

    // Clean up animators for travels that no longer exist
    for (const id of _animatorCache.keys()) {
      if (!activeIds.has(id)) _animatorCache.delete(id);
    }
  }

  if (stationedDirty) {
    _prevKeys.set(outStationed, sKey);
    outStationed.length = 0;
    for (let i = 0; i < stationed.length; i++) {
      const [wx, wz] = axialToWorld(stationed[i].end.q, stationed[i].end.r, hexRadius);
      outStationed.push({ wx, wz });
    }
  }

  return travelingDirty || stationedDirty;
}

/** Get or create a TravelAnimator for a traveling fleet. Performs catch-up on first creation. */
function _getOrCreateAnimator(
  travel: ActiveTravel, hexRadius: number, config: TravelConfig,
  planetKeys: ReadonlySet<number> | null = null,
): { animator: TravelAnimator; route: TravelRoute } | null {
  const existing = _animatorCache.get(travel.id);
  if (existing) return existing;

  const route = travel.route!;
  const hexes: { q: number; r: number }[] = route;
  const mode = travel.travelMode as TravelMode;
  let travelRoute: TravelRoute;
  try {
    travelRoute = buildTravelRoute(hexes, mode, config);
  } catch {
    return null;
  }

  const animator = new TravelAnimator(hexRadius);
  animator.start(travelRoute, planetKeys);

  // Catch-up: advance to current elapsed time
  const elapsedMs = Date.now() - Date.parse(travel.startedAt);
  const elapsedSec = Math.max(0, elapsedMs / 1000);
  if (elapsedSec > 0) {
    const r = animator.update(elapsedSec);
    // Process completed hops (like TravelCatchUp)
    let safety = MAX_CATCHUP_HOPS;
    let result = r;
    while (result.hopCompleted && !result.finished && --safety > 0) {
      result = animator.update(0);
    }
  }

  const entry = { animator, route: travelRoute };
  _animatorCache.set(travel.id, entry);
  return entry;
}

/** Tick all animators and update fleet positions. Called each frame. */
export function tickAnimators(fleets: TravelingFleetData[], dt: number): void {
  for (let i = 0; i < fleets.length; i++) {
    const f = fleets[i];
    if (!f.animator || f.finished) continue;
    const r = f.animator.update(dt);
    f.wx = r.worldX;
    f.wz = r.worldZ;
    f.dirX = r.dirX;
    f.dirZ = r.dirZ;
    f.turnRate = r.turnRate;
    if (r.finished) f.finished = true;
    // Process any hops completed in this dt
    while (r.hopCompleted && !r.finished) {
      const r2 = f.animator.update(0);
      f.wx = r2.worldX;
      f.wz = r2.worldZ;
      f.dirX = r2.dirX;
      f.dirZ = r2.dirZ;
      f.turnRate = r2.turnRate;
      if (r2.finished) { f.finished = true; break; }
      if (!r2.hopCompleted) break;
    }
  }
}

export function computeFormations(
  fleets: TravelingFleetData[], formation: FleetFormation,
  accum: Float32Array[], accumCounts: number[],
  renderers: InstancedShipRenderer[], time: number,
): void {
  for (let i = 0; i < SHIP_TYPE_COUNT; i++) accumCounts[i] = 0;
  const maxFleets = Math.min(fleets.length, MAX_FLEETS);
  for (let fi = 0; fi < maxFleets; fi++) {
    const f = fleets[fi];
    formation.compute(f.ships, f.wx, FLIGHT_HEIGHT, f.wz, f.dirX, f.dirZ, f.turnRate, time, false, false);
    const slices = formation.slices;
    for (let ti = 0; ti < SHIP_TYPE_COUNT; ti++) {
      const slice: TypeSlice = slices[ti];
      if (slice.count === 0) continue;
      const offset = accumCounts[ti] * FLOATS_PER_MAT;
      const needed = slice.count * FLOATS_PER_MAT;
      if (offset + needed > accum[ti].length) continue;
      accum[ti].set(slice.data.subarray(0, needed), offset);
      accumCounts[ti] += slice.count;
    }
  }
  for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
    renderers[i].updateInstances(accum[i], accumCounts[i]);
  }
}

export function renderStationedMarkers(
  gl: WebGL2RenderingContext, camera: Camera,
  shader: ShaderProgram, vao: WebGLVertexArrayObject,
  markers: StationedMarker[], time: number,
  color: [number, number, number], size: number, markerY: number,
): void {
  shader.use();
  shader.setMat4('u_projection', camera.projection.data);
  shader.setMat4('u_view', camera.view.data);
  shader.setFloat('u_time', time);
  shader.setFloat('u_size', size);
  shader.setFloat('u_pulse', 0.0);
  shader.setVec3('u_color', color[0], color[1], color[2]);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
  gl.depthMask(false);
  gl.bindVertexArray(vao);
  for (let i = 0; i < markers.length; i++) {
    shader.setVec3('u_worldPos', markers[i].wx, markerY, markers[i].wz);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
  gl.bindVertexArray(null);
  gl.depthMask(true);
  gl.disable(gl.BLEND);
}
